//
//  csv.ts
//
//  isana.net,inc. Proprietary and Confidential

import { IntradayIntervalMinutes } from "../utils/env";

//
export enum CsvType {
  Summary = "summary",
  Azm = "azm",
  HeartRate = "heart_rate",
  Sleep = "sleep",
  AutonomicNervousSystem = "hrv",
  Calories = "calories",
  Spo2 = "spo2",
}

export const csvTypes = [
  { type: "summary", label: "サマリー", description: "" },
  { type: "azm", label: "AZM", description: `${IntradayIntervalMinutes}分ごとのAZM` },
  { type: "heart_rate", label: "心拍数", description: `${IntradayIntervalMinutes}分ごとの心拍数` },
  // 
  // {
  //   type: "sleep",
  //   label: "睡眠",
  //   description: "睡眠レベルごとの開始/終了時間",
  // },
  {
    type: "hrv",
    label: "自律神経バランス",
    description: "5分ごとのHRV",
  },
  {
    type: "calories",
    label: "活動量(詳細)",
    description: `${IntradayIntervalMinutes}ごとの消費カロリー`,
  },
  {
    type: "spo2",
    label: "SpO2",
    description: "SpO2",
  },
] as const;



export type CsvSummaryItem = { type: string; label: string };

// Group definition for CSV summary items
export type CsvSummaryGroup = {
  title: string;
  items: CsvSummaryItem[];
};

const isIntradayNeeded = process.env.REACT_APP_INTRADAY_NEED === "true";

// 睡眠データ
const sleepDataItems: CsvSummaryItem[] = [
  { type: "bed_time", label: "ベッドにいる時間" },
  { type: "sleep_time", label: "睡眠時間" },
  { type: "sleep_start", label: "睡眠開始時間" },
  { type: "sleep_end", label: "睡眠終了時間" },
  { type: "awake_time", label: "覚醒睡眠(分)" },
  { type: "rem_sleep", label: "レム睡眠(分)" },
  { type: "light_sleep", label: "浅い睡眠(分)" },
  { type: "deep_sleep", label: "深い睡眠(分)" },
  { type: "avg_awake_time", label: "覚醒時間の平均" },
  { type: "avg_rem_sleep", label: "レム睡眠時間の平均" },
  { type: "avg_light_sleep", label: "浅い睡眠時間の平均" },
  { type: "avg_deep_sleep", label: "深い睡眠時間の平均" },
  { type: "sleep_efficiency", label: "睡眠効率" },
  { type: "sleep_latency", label: "睡眠潜時" },
  { type: "spo2_avg", label: "睡眠時のSpo2(平均)" },
  { type: "spo2_min", label: "睡眠時のSpo2(最小)" },
  { type: "spo2_max", label: "睡眠時のSpo2(最大)" },
  { type: "avg_skin_temp", label: "睡眠中の平均皮膚温(℃)" },
  { type: "resting_hr", label: "睡眠時の安静時心拍数" },
  { type: "daily_rmssd", label: "心拍数変動(RMSSD)" },
  { type: "deep_rmssd", label: "深い睡眠時の心拍数変動(RMSSD)" },
  { type: "avg_respiration", label: "睡眠時の平均呼吸数(回/分)" },
  ...(isIntradayNeeded
    ? [
      { type: "light_SleepSummary", label: "浅い睡眠時の平均呼吸数" },
      { type: "rem_SleepSummary", label: "レム睡眠時の平均呼吸数" },
      { type: "deep_SleepSummary", label: "深い睡眠時の平均呼吸数" },
    ]
    : []),
]

// 活動データ
const activityDataItems: CsvSummaryItem[] = [
  { type: "steps", label: "1日の合計歩数" },
  { type: "distance", label: "1日の合計移動距離" },
  { type: "floors", label: "1日の合計階数" },
  { type: "sitting_time", label: "1日の座っている時間(分)" },
  { type: "lightly_active_time", label: "1日の軽く活動的だった時間(分)" },
  { type: "fairly_active_time", label: "1日のかなり活動的だった時間(分)" },
  { type: "very_active_time", label: "1日の非常に活動的だった時間(分)" },
  { type: "calories_burned", label: "1日の消費カロリー(kcal)" },
  { type: "active_calories", label: "1日の活動消費カロリー(kcal)" },
  { type: "total_azm", label: "1日の合計AZM" },
  { type: "fat_burn_azm", label: "1日の脂肪燃焼心拍ゾーンのAZM" },
  { type: "cardio_azm", label: "1日の有酸素運動心拍ゾーンのAZM" },
  { type: "peak_azm", label: "1日のピーク心拍ゾーンのAZM" },
  { type: "vo2_max_upper", label: "Vo2Maxの上限値" },
  { type: "vo2_max_lower", label: "Vo2Maxの下限値" },
  {
    type: "total_steps_since_account_creation",
    label: "アカウント作成からの合計歩数",
  },
  {
    type: "total_floors_since_account_creation",
    label: "アカウント作成からの合計階数",
  },
  {
    type: "total_distance_since_account_creation",
    label: "アカウント作成からの合計距離(km)",
  },
];

// csvサマリアイテム
export const csvSummaryGroups: CsvSummaryGroup[] = [
  {
    title: "睡眠データ",
    items: sleepDataItems,
  },
  {
    title: "活動データ",
    items: activityDataItems,
  },
];

// Flat list for backward compatibility
export const csvSummaryItems: CsvSummaryItem[] = [
  ...sleepDataItems,
  ...activityDataItems,
];
